import { createUrlParameters } from '@comeon/utility';

import { getAppUrlScheme } from '~/components/app-bridge/app-properties';
import { getSwishAppRedirectUrl } from '~/components/payment/deposit/utils/swish-app-redirect-util';
import matchResponseFilter from '~/components/payment/deposit/data/deposit-response-filter';
import { browserInfo } from '~/components/payment-beta/utils';
import { getCreditCardEncryptionKey } from '~/components/payment-beta/deposit/api/credit-card';
import encryptStringWithRsaPublicKey from '~/components/payment/util/encrypt-util';
import { ROUTE_MOBILE_QUICK_DEPOSIT } from '~/components/router/constants';
import { requestApi } from '~/services/api';
import { isIOS } from '~/utils/layout-utils';
import { isChromeIOS, isFirefoxIOS, isIOSInApp } from '~/utils/user-agent-utils';

import {
	ADYEN_IDEAL,
	TRUSTLY_IDEAL,
	ADYEN_MOBILEPAY,
	CREDITCARD,
	P24,
	PAYPAL,
	SWISH,
	TRUSTLY,
	TRUSTLY_AZURA,
	ZIMPLER_SWISH,
	TRUSTLY_SWISH_NORTHMILL
} from './constants';

const API_BASE_PATH = process.env.API_BASE_PATH || '';

const encryptCreditCardInformation = async (spec, dispatch) => {
	let publicKey = null;
	await getCreditCardEncryptionKey(dispatch).then(creditCardEncryptResponse => {
		publicKey = creditCardEncryptResponse.result;
	});
	if (publicKey && publicKey !== '') {
		const encryptedExpiryMonth = await encryptStringWithRsaPublicKey(spec.vm, publicKey);
		const encryptedExpiryYear = await encryptStringWithRsaPublicKey(spec.vy, publicKey);
		const encryptedCvc = spec.cc ? await encryptStringWithRsaPublicKey(spec.cc, publicKey) : '';
		return {
			...spec,
			vm: encryptedExpiryMonth,
			vy: encryptedExpiryYear,
			cc: encryptedCvc
		};
	}
	// eslint-disable-next-line no-console
	console.error('error while getting credit card encryption key');
	return spec;
};

const getIOSRedirect = () => {
	// for ios chrome & ios mozilla, change the protocol to chrome to prevent redirection to safari
	const url = isChromeIOS() ? 'googlechromes' : isFirefoxIOS() ? 'firefox' : null;

	return url;
};

const getReturnScheme = () => {
	const appUrlScheme = getAppUrlScheme();

	switch (true) {
		case !!appUrlScheme: // redirect for our native apps
			return appUrlScheme;
		case !window.appProperties && isIOSInApp(): // for in-app browsers on ios except our app
			return null;
		case isIOS():
			return getIOSRedirect();

		default:
			return null;
	}
};

const renderPayload = async (providerData, dispatch) => {
	let payload = {
		am: providerData.amount || '',
		paymentMethodId: providerData.depositMethodId,
		utimestamp: new Date().getTime(),
		depositType: providerData.depositType ?? 1
	};

	switch (providerData.paymentMethodName) {
		case PAYPAL: {
			const { enableOneClick, cu = '', depositLimitsAndFeesData = {}, OrderFuniquedID = '' } = providerData;
			payload = {
				...payload,
				cu: depositLimitsAndFeesData.depositLimitData?.PARAM_PLAYER_CURRENCY || cu,
				enableOneClick,
				orderFValue: OrderFuniquedID,
				finalPlayerReturnPath: `/${ROUTE_MOBILE_QUICK_DEPOSIT}`
			};
			break;
		}

		case ADYEN_IDEAL:
		case TRUSTLY_IDEAL:
		case P24:
		case TRUSTLY:
		case TRUSTLY_AZURA:
		case ZIMPLER_SWISH:
			payload = {
				...payload,
				finalPlayerReturnPath: `/${ROUTE_MOBILE_QUICK_DEPOSIT}`,
				returnScheme: getReturnScheme()
			};
			break;

		case SWISH:
		case TRUSTLY_SWISH_NORTHMILL: {
			const { swishQRCodeFlow, extraParameters = {} } = providerData;
			const { phoneNumber, countryCode } = extraParameters;
			payload = {
				...payload,
				...(swishQRCodeFlow ? { swishMobileFlow: true } : { phoneNumber, countryCode }),
				swishReturnPath: getSwishAppRedirectUrl()
			};
			break;
		}

		case ADYEN_MOBILEPAY: {
			const { extraParameters = {} } = providerData;
			const { phoneNumber, countryCode } = extraParameters;
			payload = {
				...payload,
				phoneNumber,
				countryCode,
				finalPlayerReturnPath: `/${ROUTE_MOBILE_QUICK_DEPOSIT}`
			};
			break;
		}

		case CREDITCARD: {
			const { playerCardData, cvv, bypassCvv } = providerData;
			const {
				depositLimitsAndFeesData: { paymentProviderData = null }
			} = providerData;
			let paymentMethodId = providerData.depositMethodId;
			let providerId;

			if (paymentProviderData) {
				paymentMethodId = paymentProviderData.PARAM_PAYMENT_METHOD_ID;
				providerId = paymentProviderData.PARAM_PAYMENT_PROVIDER_ID;
			}

			const cardDetails = {
				...(playerCardData || {}),
				cc: cvv
			};
			const { id, vm, vy, cc } = await encryptCreditCardInformation(cardDetails, dispatch);

			payload = {
				...payload,
				paymentMethodId,
				providerId,
				instantDeposit: true,
				cid: id,
				bypassCvv,
				cc,
				vm,
				vy,
				browserInfo,
				finalPlayerReturnPath: `/${ROUTE_MOBILE_QUICK_DEPOSIT}`
			};
			break;
		}

		default:
			break;
	}

	return payload;
};

export const checkSchufaConsent = async (amount, playerId) => {
	try {
		const url = `${API_BASE_PATH}/PaymentFormDataService/getDepositFeesAndLimits/schufaDepositLimit`;
		const response = await requestApi(
			url,
			createUrlParameters({
				amount,
				playerId
			}),
			'POST',
			true
		);
		if (response?.playerConsentNeeded) {
			return true;
		}
		return false;
	} catch (error) {
		// eslint-disable-next-line no-console
		console.log(error);
		return false;
	}
};

const executeInstantDeposit = async (data, dispatch) => {
	const payload = await renderPayload(data, dispatch);
	let result = {};

	try {
		const response = await requestApi(data.executeDepositUrl, payload, 'POST');
		if (response.successful) {
			const { sendHandlerData } = response || {};
			result = matchResponseFilter(
				response,
				Object.keys(sendHandlerData).includes('handler')
					? sendHandlerData?.handler?.actualSendHandler
					: sendHandlerData?.actualSendHandler
			);
			result.successful = true;
			result.methodDetails = { methodCode: data.paymentMethodName };
			return result;
		}
		return response;
	} catch (e) {
		return { successful: false, e };
	}
};

export default executeInstantDeposit;
